import * as React from "react";
import {useContext} from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import {FormAlertModel} from "../../api/types/alert";
import AlertFormsHandler from "./AlertForms/AlertFormsHandler";
import DailyScheduler from "./Scheduling/DailyScheduler";
import WeeklyScheduler from "./Scheduling/WeeklyScheduler";
import MonthlyScheduler from "./Scheduling/MonthlyScheduler";
import {useFormContext} from "react-hook-form";
import {Grid} from "@mui/material";
import {useRecoilState} from "recoil";
import {initialFormAlertModel, schedulingAtom} from "../../state/alertRecoil";
import {SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";

type Props = {
    setFormDialog: Function;
    handleConfirmSave: Function;
    readonly: boolean;
};

export default function LinearStepper({
                                          setFormDialog,
                                          handleConfirmSave,
                                          readonly,
                                      }: Props) {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    // Use Recoil
    const [schedulingList] = useRecoilState(schedulingAtom);

    const steps = [
        t("pricing:alert.steps.choose"),
        t("pricing:alert.steps.configure"),
        t("pricing:alert.steps.schedule"),
    ];

    //USE FORM CONTEXT
    const {reset, watch, trigger, getValues, clearErrors} =
        useFormContext<FormAlertModel>();

    //START STEP MANAGMENT
    const [activeStep, setActiveStep] = React.useState(0);

    const triggerLocationFields = () => {
        if (
            watch("locationGroups")?.length === 0 &&
            watch("locations")?.length === 0
        ) {
            return trigger("locations"), trigger("locationGroups");
        } else if (watch("locations")?.length > 0) {
            return trigger("locations");
        } else if (watch("locationGroups")?.length > 0) {
            return trigger("locationGroups");
        }
    };

    const customTrigger = () => {
        if (
            getValues().capacityOfLocations.remainingPercentage &&
            getValues().capacityOfLocations.remainingCapacity &&
            getValues().capacityOfLocations.usagePercentage
        ) {
            return (
                trigger("capacityOfLocations.remainingPercentage"),
                    trigger("capacityOfLocations.remainingCapacity"),
                    trigger("capacityOfLocations.usagePercentage")
            );
        } else if (
            getValues().capacityOfLocations.remainingPercentage &&
            getValues().capacityOfLocations.remainingCapacity
        ) {
            return (
                trigger("capacityOfLocations.remainingPercentage"),
                    trigger("capacityOfLocations.remainingCapacity"),
                    trigger("capacityOfLocations.usagePercentage")
            );
        } else if (
            getValues().capacityOfLocations.remainingPercentage &&
            getValues().capacityOfLocations.usagePercentage
        ) {
            return (
                trigger("capacityOfLocations.remainingPercentage"),
                    trigger("capacityOfLocations.usagePercentage"),
                    trigger("capacityOfLocations.remainingCapacity")
            );
        } else if (getValues().capacityOfLocations.remainingPercentage) {
            return trigger("capacityOfLocations.remainingPercentage");
        } else if (getValues().capacityOfLocations.remainingCapacity) {
            return (
                trigger("capacityOfLocations.remainingCapacity"),
                    trigger("capacityOfLocations.usagePercentage")
            );
        } else if (getValues().capacityOfLocations.usagePercentage) {
            return (
                trigger("capacityOfLocations.usagePercentage"),
                    trigger("capacityOfLocations.remainingCapacity")
            );
        } else {
            return (
                trigger("capacityOfLocations.remainingPercentage"),
                    trigger("capacityOfLocations.remainingCapacity"),
                    trigger("capacityOfLocations.usagePercentage")
            );
        }
    };

    const triggerFirstType = async () => {
        const description = await trigger("description");
        const recipientLists = await trigger("recipientLists");
        const schedulingType = await trigger("schedulingType");
        const remainingCapacityDays = await trigger(
            "capacityOfLocations.remainingCapacityDays"
        );
        const triggerCustom = await customTrigger();
        const triggerLocation = await triggerLocationFields();
        const dealer = await trigger("dealer");
        const legalEntity = await trigger("legalEntity");

        if (
            description &&
            recipientLists &&
            schedulingType &&
            remainingCapacityDays &&
            triggerCustom &&
            triggerLocation &&
            dealer &&
            legalEntity
        ) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const triggerSecondType = async () => {
        const description = await trigger("description");
        const recipientLists = await trigger("recipientLists");
        const schedulingType = await trigger("schedulingType");
        const maxDailyBookings = await trigger(
            "availabilityOfVehicles.maxDailyBookings"
        );
        const securityMargin = await trigger(
            "availabilityOfVehicles.securityMargin"
        );
        const triggerLocation = await triggerLocationFields();
        const dealer = await trigger("dealer");
        const legalEntity = await trigger("legalEntity");

        if (
            description &&
            recipientLists &&
            schedulingType &&
            maxDailyBookings &&
            securityMargin &&
            triggerLocation &&
            dealer &&
            legalEntity
        ) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const triggerThirdType = async () => {
        if (readonly) {
            // reset form
            reset(initialFormAlertModel(t, legalEntityOptions, dealerOption));
            setFormDialog(null);
        } else {
            const description = await trigger("description");
            const recipientLists = await trigger("recipientLists");
            const topPrice = await trigger("competitorsBenchmarks.topPricePosition");
            const bottomPrice = await trigger(
                "competitorsBenchmarks.bottomPricePosition"
            );
            const dealer = await trigger("dealer");
            const legalEntity = await trigger("legalEntity");

            if (
                description &&
                recipientLists &&
                topPrice &&
                bottomPrice &&
                dealer &&
                legalEntity
            ) {
                handleConfirmSave();
            }
        }
    };

    const triggerFourthType = async () => {
        const description = await trigger("description");
        const recipientLists = await trigger("recipientLists");
        const schedulingType = await trigger("schedulingType");
        const dateFrom = await trigger("rpd.pickupDateFrom");
        const dateTo = await trigger("rpd.pickupDateTo");
        const minRPD = await trigger("rpd.minRPD");
        const triggerLocation = await triggerLocationFields();
        const dealer = await trigger("dealer");
        const legalEntity = await trigger("legalEntity");

        if (
            description &&
            recipientLists &&
            schedulingType &&
            dateFrom &&
            dateTo &&
            minRPD &&
            triggerLocation &&
            dealer &&
            legalEntity
        ) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const triggerFifthType = async () => {
        const description = await trigger("description");
        const recipientLists = await trigger("recipientLists");
        const schedulingType = await trigger("schedulingType");
        const dateFrom = await trigger("pricingErrors.pickupDateFrom");
        const dateTo = await trigger("pricingErrors.pickupDateTo");
        const minDailyPrice = await trigger("pricingErrors.minDailyPrice");
        const unitType = await trigger("pricingErrors.unitType");
        const triggerLocation = await triggerLocationFields();
        const dealer = await trigger("dealer");
        const legalEntity = await trigger("legalEntity");

        if (
            description &&
            recipientLists &&
            schedulingType &&
            dateFrom &&
            dateTo &&
            minDailyPrice &&
            unitType &&
            triggerLocation &&
            dealer &&
            legalEntity
        ) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const triggerSixthType = async () => {
        const description = await trigger("description");
        const recipientLists = await trigger("recipientLists");
        const schedulingType = await trigger("schedulingType");
        const dateFrom = await trigger("advanceBooking.pickupDateFrom");
        const dateTo = await trigger("advanceBooking.pickupDateTo");
        const n = await trigger("advanceBooking.pastDays");
        const k = await trigger("advanceBooking.pastOrFutureDays");
        const averageBookingsNumber = await trigger(
            "advanceBooking.averageBookingsNumber"
        );
        const triggerLocation = await triggerLocationFields();
        const dealer = await trigger("dealer");
        const legalEntity = await trigger("legalEntity");
        if (
            description &&
            recipientLists &&
            schedulingType &&
            dateFrom &&
            dateTo &&
            n &&
            k &&
            averageBookingsNumber &&
            triggerLocation &&
            dealer &&
            legalEntity
        ) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleNext = async () => {
        if (activeStep === 0) {
            const result = await trigger("type");
            if (result) {
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
        } else if (
            activeStep === 1 &&
            watch("type")?.label === "Capacity of the locations and fleet utilization"
        ) {
            triggerFirstType();
        } else if (
            activeStep === 1 &&
            watch("type")?.label ===
            "Availability of vehicles in the locations and fleet utilization"
        ) {
            triggerSecondType();
        } else if (
            activeStep === 1 &&
            watch("type")?.label === "Competitors benchmarks"
        ) {
            triggerThirdType();
        } else if (
            activeStep === 1 &&
            watch("type")?.label === "Revenue per day for locations and pick-up date"
        ) {
            triggerFourthType();
        } else if (activeStep === 1 && watch("type")?.label === "Pricing errors") {
            triggerFifthType();
        } else if (activeStep === 1 && watch("type")?.label === "Advance booking") {
            triggerSixthType();
        }
        // terzo step
        else if (activeStep === 2) {
            if (readonly) {
                // reset form
                reset(initialFormAlertModel(t, legalEntityOptions, dealerOption));
                setFormDialog(null);
            } else {
                //giornaliero
                if (watch("schedulingType")?.label === schedulingList[0].label) {
                    const startTime = await trigger("crons.startTime");
                    if (startTime) {
                        handleConfirmSave();
                    }
                }
                // mensile
                else if (watch("schedulingType")?.label === schedulingList[1].label) {
                    if (watch("crons.radioType") === t("pricing:alert.labels.every")) {
                        const monthlyDays = await trigger("crons.monthlyDays");
                        const startTime = await trigger("crons.startTime");
                        if (startTime && monthlyDays) {
                            handleConfirmSave();
                        }
                    } else {
                        const startTime = await trigger("crons.startTime");
                        if (startTime) {
                            handleConfirmSave();
                        }
                    }
                }
                // settimanale
                else if (watch("schedulingType")?.label === schedulingList[2].label) {
                    const weeklyDays = await trigger("crons.weeklyDays");
                    const startTime = await trigger("crons.startTime");
                    if (startTime && weeklyDays) {
                        handleConfirmSave();
                    }
                }
            }
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    //FUNZIONE DI RENDER PER I SCHEDULER
    const renderScheduler = () => {
        const DAILY = schedulingList[0].label;
        const MONTHLY = schedulingList[1].label;
        const WEEKLY = schedulingList[2].label;

        switch (watch("schedulingType")?.label) {
            case DAILY:
                return <DailyScheduler readonly={readonly}/>;
            case WEEKLY:
                return <WeeklyScheduler readonly={readonly}/>;
            case MONTHLY:
                return <MonthlyScheduler readonly={readonly}/>;
        }
    };

    return (
        <Box sx={{width: "100%"}}>
            <Stepper activeStep={activeStep} sx={{width: "100%"}}>
                {steps.map((label, index) => {
                    const stepProps: {
                        completed?: boolean;
                        disabled?: boolean;
                    } = {
                        disabled:
                            watch("type")?.label === "Competitors benchmarks" && index === 2,
                    };
                    const labelProps: {
                        optional?: React.ReactNode;
                    } = {};
                    return (
                        <Step
                            key={label}
                            {...stepProps}
                            style={{
                                width: `calc(100% / ${steps.length})`,
                            }}
                        >
                            <StepLabel {...labelProps}>{label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            <Grid container>
                {activeStep < 2 ? (
                    <AlertFormsHandler activeStep={activeStep} readonly={readonly}/>
                ) : (
                    renderScheduler()
                )}
                <Grid item xs={12} display={"flex"} justifyContent={"flex-end"} mt={3}>
                    {activeStep !== 0 ? (
                        <Button
                            color="inherit"
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            sx={{mr: 1}}
                        >
                            {t("pricing:alert.buttons.back")}
                        </Button>
                    ) : (
                        ""
                    )}
                    <Button onClick={handleNext}>
                        {activeStep === 0
                            ? t("pricing:alert.buttons.next")
                            : readonly &&
                            (activeStep === 2 ||
                                (activeStep === 1 &&
                                    getValues().type?.code === "COMPETITORS_BENCHMARK"))
                                ? t("pricing:actions.close")
                                : t("pricing:alert.buttons.confirm")}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
}
